/**
 * Where the sheet's chosen showing actually is — its building, its street,
 * its coordinates, its booking link (UI-audit a1-01).
 *
 * A film playing eleven cinemas is one row, and the row's `venue`, `street`,
 * `lat`/`lng` and `booking_url` are one member's: cinematic-berlin's
 * VATERLAND row is filed at Il Kino and carries Il Kino's kinoheld link. The
 * sheet read every place fact off the row, so picking Passage Neukölln's
 * 17:00 left Where, Directions and Tickets at Il Kino, and the `.ics` paired
 * "Passage Neukölln" with Il Kino's Nansenstraße — the row's place doing two
 * jobs, "where this row is filed" and "where the night you picked is".
 *
 * Doug's NEXT.md question, "should Where/Directions follow the selected
 * chip?", answered yes: everything place-specific follows the highlighted
 * showing, and the row is the answer only where there is no showing, or the
 * showing is at the row's own venue.
 *
 * Dependency-free on purpose, so the Worker's suite can load it (`ics.ts`
 * imports it, and `test/sheet-place.test.ts` tests it).
 */

/** The row, as far as where it is goes. */
export interface PlaceRow {
  venue?: string | null
  venue_id?: string | null
  building_id?: string | null
  street?: string | null
  postcode?: string | null
  lat?: number | null
  lng?: number | null
  booking_url?: string | null
  url?: string | null
}

/** One showing, as `showing_list` carries it. */
export interface PlaceShowing {
  venue?: string | null
  venue_id?: string | null
  building_id?: string | null
  url?: string | null
}

/** A `venues` row — `useVenues()`'s, reduced to what an address needs. */
export interface PlaceVenue {
  name?: string | null
  parent_id?: string | null
  street?: string | null
  postcode?: string | null
  lat?: number | null
  lng?: number | null
}

export interface SheetPlace {
  /** The place page this place opens (`#/places/<id>`, a1-28): the
   *  building's id, else the venue's; null where there is no venue row. */
  id: string | null
  /** True when this is the row's own place, and every row-level fact —
   *  the ticket check, the shop it found, `booking_url` — still applies. */
  own: boolean
  /** True for the row's own building — its venue, or another hall of it:
   *  the same box office, so the row's ticket check and its shop still
   *  apply. False only for another house altogether. */
  house: boolean
  venue: string | null
  street: string | null
  postcode: string | null
  lat: number | null
  lng: number | null
  /** Where Tickets goes for this place, or null where there is nowhere.
   *  Usually a ticket page; for a night at another cinema with no page of
   *  its own it is the listing site's film page instead (tix-1), and `via`
   *  then says so. */
  booking: string | null
  /** The listing site's short name ("cinematic") when `booking` is its film
   *  page rather than this place's own ticket page, else null. Its own
   *  field, not inferred from `booking` by the caller: "where Tickets goes"
   *  and "is that a ticket page" disagree exactly on these nights, and a
   *  label read off the URL would be the one value doing both jobs. */
  via: string | null
  /** A page about the event at this place: the booking page, else the page
   *  the showing was read from, else the row's. The `.ics` URL. */
  page: string | null
}

const absolute = (u: string | null | undefined): string | null => {
  if (!u) return null
  try { return new URL(u).host ? u : null } catch { return null }
}

/**
 * A listing site's name as a label says it: the host without `www.`, its
 * subdomains, top-level domain, or a trailing `-berlin` — `www.cinematic-berlin.com` is
 * "cinematic", `kinoheld.de` is "kinoheld". Never empty: a host that is only
 * "berlin.de" stays "berlin". Null for a URL with no host.
 */
export function siteName(url: string | null | undefined): string | null {
  if (!url) return null
  let host: string
  try { host = new URL(url).host.toLowerCase() } catch { return null }
  if (!host) return null
  const labels = host.replace(/^www\./, '').replace(/:\d+$/, '').split('.')
  const name = labels.length > 1 ? labels[labels.length - 2] : labels[0]
  return name.replace(/[-.]?berlin$/, '') || name
}

/** Is this showing at the row's own venue? A showing that names no venue
 *  at all is treated as the row's: there is nothing else it could be. */
function atRowVenue(row: PlaceRow, s: PlaceShowing): boolean {
  if (s.venue_id && row.venue_id) return s.venue_id === row.venue_id
  if (!s.venue_id && !s.venue) return true
  return !!s.venue && s.venue === row.venue
}

/**
 * The place for the highlighted showing, or for the row where there is none.
 *
 * A showing somewhere else gets that venue's own address and coordinates
 * from `venueOf` (a hall with none borrows its building's), and never the
 * row's — a venue the lookup does not have yet is named with no street and
 * no Directions, rather than with Il Kino's. Its booking link is the
 * showing's own `url` where that is a page of its own. The row's
 * `booking_url` is never lent to another house — except a hall of the row's
 * own building, which is the same box office.
 *
 * tix-1, Doug chose B: where the showing only repeats the row's source page
 * — cinematic-berlin gives one film page for every cinema it lists — Tickets
 * opens that page, which links every cinema, and `via` names the site so the
 * button can say it is not this cinema's own. Before, those nights had no
 * Tickets at all (WP 10.7's "a source page is not a ticket page"); that rule
 * still holds for the row's own place, which this does not touch.
 */
export function sheetPlace(
  row: PlaceRow,
  showing: PlaceShowing | null | undefined,
  venueOf: (id: string) => PlaceVenue | null | undefined = () => null,
): SheetPlace {
  if (!showing || atRowVenue(row, showing)) {
    return {
      id: row.building_id ?? row.venue_id ?? null,
      own: true,
      house: true,
      venue: row.venue ?? null,
      street: row.street ?? null,
      postcode: row.postcode ?? null,
      lat: row.lat ?? null,
      lng: row.lng ?? null,
      booking: row.booking_url ?? null,
      via: null,
      page: row.booking_url ?? row.url ?? null,
    }
  }

  const v = showing.venue_id ? venueOf(showing.venue_id) ?? null : null
  const parent = v?.parent_id ? venueOf(v.parent_id) ?? null : null
  // Coordinates travel as a pair: a hall's latitude with its building's
  // longitude is a point in neither.
  const own = v?.lat != null && v?.lng != null
  const lat = own ? v!.lat! : parent?.lat != null && parent?.lng != null ? parent.lat : null
  const lng = own ? v!.lng! : parent?.lat != null && parent?.lng != null ? parent.lng : null

  const rowHouse = row.building_id ?? row.venue_id ?? null
  const house = showing.building_id ?? showing.venue_id ?? null
  const sameHouse = !!rowHouse && rowHouse === house

  const shown = absolute(showing.url)
  const ownPage = shown && shown !== row.url ? shown : null
  const boxOffice = sameHouse ? row.booking_url ?? null : null
  const booking = ownPage ?? boxOffice ?? shown
  const via = !ownPage && !boxOffice && shown ? siteName(shown) : null

  return {
    id: showing.building_id ?? showing.venue_id ?? null,
    own: false,
    house: sameHouse,
    venue: showing.venue ?? v?.name ?? null,
    street: v?.street ?? parent?.street ?? null,
    postcode: v?.postcode ?? parent?.postcode ?? null,
    lat,
    lng,
    booking,
    via,
    page: booking ?? shown ?? row.url ?? null,
  }
}

/** The Where line's address — the same three parts `sheetFacts` joins. */
export function placeAddress(p: Pick<SheetPlace, 'venue' | 'street' | 'postcode'>): string {
  return [p.venue, p.street, p.postcode].filter(Boolean).join(', ')
}
